import axios from 'axios';

import { capabilitiesRegistry, getCapabilitiesForBackend } from './capabilities/capabilities-registry';

/**
 * @typedef {object} ShippyMessage
 * @property {string} role - The role of the message sender (MESSAGE_ROLES.USER or MESSAGE_ROLES.ASSISTANT)
 * @property {string} content - The content of the message
 */

/**
 * @typedef {object} ShippyContext
 * @property {string} currentRoute - Current page route
 * @property {Array<object>} connectedStores - User's connected stores
 */

/**
 * @typedef {object} ShippyCapabilityResponse
 * @property {string} id - Capability ID
 * @property {number} confidence - Confidence score 0-1
 * @property {Record<string, unknown>} parameters - Parameters for the capability
 */

/**
 * @typedef {object} ShippyClarificationOption
 * @property {string} label - Display text for the option
 * @property {string} value - Value to use when option is selected (for follow-up message)
 * @property {string} [commandId] - If present, navigate directly to this command without another API call
 */

/**
 * @typedef {object} ShippyClarificationResponse
 * @property {string} question - Question to ask the user
 * @property {ShippyClarificationOption[]} options - Available options
 */

/**
 * @typedef {object} ShippyApiResponse
 * @property {boolean} isSuccess - Whether the request succeeded
 * @property {string} message - Text response from Shippy
 * @property {ShippyCapabilityResponse} [capability] - Capability to execute
 * @property {ShippyClarificationResponse} [clarification] - Clarification request
 */

/**
 * Send chat message to Shippy backend
 * @param {string} message - User's message
 * @param {ShippyContext} context - Current application context
 * @param {ShippyMessage[]} conversationHistory - Previous messages
 * @param {string[]} sellerFeatures - Array of enabled seller features (from Redux)
 * @param {object} capabilityContexts - Dynamic contexts for capabilities (from useCapabilityContext)
 * @param {string[]} userRoles - User's assigned roles (from Redux)
 * @param {string} conversationId - UUID identifying the conversation session
 * @param {number} turnNumber - 1-based turn number within the conversation
 * @param {Record<string, object>} [additionalCapabilities] - Additional capabilities from the LLM action registry
 * @returns {Promise<ShippyApiResponse>} Shippy response
 */
export const sendShippyMessage = async (
  message,
  context,
  conversationHistory = [],
  sellerFeatures = [],
  capabilityContexts = {},
  userRoles = [],
  conversationId,
  turnNumber,
  additionalCapabilities = {},
) => {
  const capabilities = getCapabilitiesForBackend(sellerFeatures, userRoles, capabilityContexts);

  const requestBody = {
    message,
    clientContext: context,
    conversationHistory,
    capabilitiesVersion: capabilitiesRegistry.version,
    availableCapabilities: { ...capabilities, ...additionalCapabilities },

    // Conversation tracking
    conversationId,
    turnNumber,
  };

  const response = await axios.post('shippy/chat', requestBody);
  return response.data;
};
